// src/oura/components/client/RequestsCard.tsx
//
// Requests: everything the client started, in one card. The changes they
// asked for from their Work page ("Ask for a change"), and the calls they
// asked for. Grouped by who moves next: what needs us (a change stays here
// until it is Done, replied to or not), the calls we scheduled, and what is
// done, folded away. A row is their words and 1 fact; each opens its own
// sheet in the AskDrawer, which holds who asked, our reply and the files.
//
// What we asked the client is the other card, Questions to the client
// (AsksCard.tsx). The screen reads the requests once and hands them down, so
// this card, the page sentence and the drawer never disagree. The list is a
// result: "cannot be read yet" (live, before the route ships) never shows as
// "Nothing asked yet".

import { Loader2Icon, MessageSquarePlusIcon, PaperclipIcon, PhoneIcon } from "lucide-react";
import type { CallRequest, ChangeRequest } from "@service/aeo/clientReview";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { AREA_TEXT, DOC_STATUS, FamilyChip, FoldCard, GroupFold, ListRow, REQUEST_STATUS, ShowMore, TYPE, Tag } from "@oura/ui";
import { docsFailureText, extraSmallChangeIds, needUsText, trimText, whenText, type DocsLoad } from "@oura/docs";
import type { AskSeq, AskTarget } from "./askTargets";
import {
    CALL_STATUS,
    isFresh,
    repliedText,
    requestStatus,
    type ClientRequest,
    type RequestGroups,
    type UnreadMarks,
} from "./changeRequests";

/** Rows shown in a finished group before "Show N more". */
const SHOWN = 3;

/** The group heads, in the Documents board's and the tags' colours. */
const NEEDS_US_TONE = DOC_STATUS.needs_us.staff.tone;
const SCHEDULED_TONE = CALL_STATUS.scheduled.tone;
const DONE_TONE = REQUEST_STATUS.done.tone;

/** The group heads' words, which a row says to a screen reader only. */
const GROUP_TITLE = { needs_us: "Needs us", scheduled: "Calls scheduled", done: "Done" } as const;
type RequestGroup = keyof typeof GROUP_TITLE;

/** Opens a request with the group its row sat in, so the drawer walks that group. */
type OpenRequest = (t: AskTarget, seq: AskSeq) => void;

/** A group's snapshot for the drawer: its rows' keys in the order this card shows them. */
function seqOf(group: RequestGroup, rows: ClientRequest[]): AskSeq {
    return { groupId: `requests.${group}`, label: GROUP_TITLE[group], keys: rows.map((r) => `${r.kind}:${r.id}`) };
}

/**
 * A change the client asked for, in their words, with 1 fact: when they
 * asked, when we replied, or when it was done. The small change carries its
 * G5 chip; a violet tag only for one filed for another month or as an extra.
 * Who asked, our reply and the files by name are in the drawer.
 */
function ChangeRow({ change, group, fresh, extra, onOpen }: { change: ChangeRequest; group: RequestGroup; fresh: boolean; extra: boolean; onOpen: (t: AskTarget) => void }) {
    const status = requestStatus(change, undefined, extra);
    // "This month's small change" is the G5 chip alone; another month's, or an extra, says so.
    const odd = change.smallChange && (status.label.startsWith("Extra small change") || status.label.startsWith("Small change for"));
    const names = change.files.map((f) => f.name).filter(Boolean);
    const files = change.files.length;
    const meta = change.status === "done" ? dayDate(change.doneAt) : change.reply ? repliedText(change.reply.at) : whenText(change.at);
    const tags = (fresh || change.smallChange || files > 0) && (
        <>
            {fresh && <Tag tone="blue">New</Tag>}
            {change.smallChange && <FamilyChip family="g">G5</FamilyChip>}
            {files > 0 && (
                <span title={names.join(", ")} className={cn(TYPE.tm, "inline-flex items-center gap-1 tabular-nums text-gray-500")}>
                    <PaperclipIcon aria-hidden size={12} className="shrink-0" />
                    {files}
                    <span className="sr-only">{files === 1 ? " file" : " files"}</span>
                </span>
            )}
            {odd && <Tag tone={status.tone}>{status.label}</Tag>}
        </>
    );
    return (
        <ListRow
            lead="icon"
            stackMeta
            onClick={() => onOpen({ kind: "change", changeId: change.id, change })}
            data-change-id={change.id}
            icon={<MessageSquarePlusIcon aria-hidden size={14} className={AREA_TEXT.asks} />}
            title={`\u201c${trimText(change.note, 140)}\u201d`}
            srStatus={GROUP_TITLE[group]}
            tags={tags || undefined}
            meta={meta ?? undefined}
        />
    );
}

/** A call the client asked for, on a question when it names one. Their preference is in the drawer. */
function CallRow({ call, group, about, fresh, onOpen }: { call: CallRequest; group: RequestGroup; about: string | null; fresh: boolean; onOpen: (t: AskTarget) => void }) {
    const note = call.note.trim();
    return (
        <ListRow
            lead="icon"
            stackMeta
            onClick={() => onOpen({ kind: "call", callId: call.id })}
            data-call-id={call.id}
            icon={<PhoneIcon aria-hidden size={14} className={AREA_TEXT.people} />}
            // The question in quotes, so it reads as what they asked about.
            title={about ? `Call about \u201c${about}\u201d` : note ? `Call: \u201c${trimText(note, 140)}\u201d` : "Call request"}
            srStatus={GROUP_TITLE[group]}
            tags={fresh ? <Tag tone="blue">New</Tag> : undefined}
            meta={whenText(call.at) ?? undefined}
        />
    );
}

export function RequestsCard({
    groups,
    requests,
    callsLoading,
    callsFailed,
    marks,
    itemTitle,
    businessName,
    onOpen,
}: {
    groups: RequestGroups;
    /** The change requests' read: loading, a failure, or "not served yet" each say so. */
    requests: Pick<DocsLoad<ChangeRequest[]>, "status" | "data" | "error">;
    /** The call requests come with the screen's first read. */
    callsLoading: boolean;
    /** That read failed: the calls here may be short. */
    callsFailed: boolean;
    /** What the bell has not marked read: a "New" tag on a request that still needs us. */
    marks: UnreadMarks;
    /** The title of the question a call is about, when it is still in a pack. */
    itemTitle: (itemId: string) => string | null;
    businessName: string;
    onOpen: OpenRequest;
}) {
    // Filed after the month already had its small change: "Extra small change".
    const extra = extraSmallChangeIds(requests.data ?? []);
    const seqs = { needs_us: seqOf("needs_us", groups.needsUs), scheduled: seqOf("scheduled", groups.scheduled), done: seqOf("done", groups.done) };
    const row = (r: ClientRequest, group: RequestGroup, fresh = false) => {
        const open = (t: AskTarget) => onOpen(t, seqs[group]);
        return r.kind === "change" ? (
            <ChangeRow key={`change-${r.id}`} change={r.change} group={group} fresh={fresh} extra={extra.has(r.id)} onOpen={open} />
        ) : (
            <CallRow key={`call-${r.id}`} call={r.call} group={group} about={r.call.itemId ? itemTitle(r.call.itemId) : null} fresh={fresh} onOpen={open} />
        );
    };

    const busy = requests.status === "loading" || callsLoading;
    const problem = requests.error
        ? { text: docsFailureText(requests.error, "the client's requests"), notServed: requests.error.reason === "notServed" }
        : null;
    const needsUs = groups.needsUs.length;
    const total = needsUs + groups.scheduled.length + groups.done.length;
    // "Nothing asked yet" only once both reads came back and hold nothing.
    const none = total === 0 && requests.status === "ready" && !requests.error && !callsFailed;

    return (
        <FoldCard
            id="requests"
            anchor="requests"
            title="Requests"
            icon={{ icon: MessageSquarePlusIcon, area: "asks" }}
            foldedFact={!busy && (needsUs > 0 ? needUsText(needsUs) : total > 0 && "Nothing needs us")}
        >
            {!busy && problem && !problem.notServed && (
                // First, above the rows it qualifies: they leave out every
                // request that could not be read.
                <p className={cn(TYPE.tc, "m-0 mb-3 text-amber-700")}>{problem.text}</p>
            )}
            {busy ? (
                <p className={cn(TYPE.tb, "m-0 flex items-center gap-2 text-gray-400")}>
                    <Loader2Icon aria-hidden size={16} className="animate-spin" /> Loading
                </p>
            ) : none ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>
                    Nothing asked yet. {businessName} asks from their Work page with Ask for a change.
                </p>
            ) : total === 0 ? (
                // Unknown, never "nothing": a read failed or is not served yet,
                // and the one line above or below this says which. Never a second one.
                null
            ) : (
                // The groups are the only children of this box, so the first loses its divider.
                <div>
                    {needsUs > 0 && (
                        <GroupFold id="requests.needs_us" lead="icon" tone={NEEDS_US_TONE} title={GROUP_TITLE.needs_us} count={needsUs}>
                            <div>{groups.needsUs.map((r) => row(r, "needs_us", isFresh(r, marks)))}</div>
                        </GroupFold>
                    )}
                    {groups.scheduled.length > 0 && (
                        <GroupFold id="requests.scheduled" lead="icon" tone={SCHEDULED_TONE} title={GROUP_TITLE.scheduled} count={groups.scheduled.length}>
                            <div>{groups.scheduled.slice(0, SHOWN).map((r) => row(r, "scheduled"))}</div>
                            <ShowMore count={groups.scheduled.length - SHOWN}>{groups.scheduled.slice(SHOWN).map((r) => row(r, "scheduled"))}</ShowMore>
                        </GroupFold>
                    )}
                    {groups.done.length > 0 && (
                        <GroupFold id="requests.done" lead="icon" tone={DONE_TONE} title={GROUP_TITLE.done} count={groups.done.length} defaultFolded>
                            <div>{groups.done.slice(0, SHOWN).map((r) => row(r, "done"))}</div>
                            <ShowMore count={groups.done.length - SHOWN}>{groups.done.slice(SHOWN).map((r) => row(r, "done"))}</ShowMore>
                        </GroupFold>
                    )}
                </div>
            )}
            {!busy && callsFailed && (
                <p className={cn(TYPE.tc, "m-0 text-amber-700", total > 0 && "mt-3")}>Could not read the call requests, so this list may be short.</p>
            )}
            {!busy && problem?.notServed && (
                // Named, so a short list is not read as the whole story. "Not
                // served yet" is a fact, not a fault: grey, below the list.
                <p className={cn(TYPE.tc, "m-0 text-gray-500", total > 0 && "mt-3")}>{problem.text}</p>
            )}
        </FoldCard>
    );
}
